import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const ACTIVITY_EVENTS = [
  'mousemove',
  'click',
  'keyup',
  'touchstart',
  'touchmove',
]
const DEFAULT_INACTIVITY_TIMEOUT = 3

interface UseDetectInactivityArgs {
  disable?: boolean
  timeoutInSeconds?: number
  activityEvents?: string[]
}

const useDetectInactivity = (args?: UseDetectInactivityArgs) => {
  const {
    disable,
    timeoutInSeconds = DEFAULT_INACTIVITY_TIMEOUT,
    activityEvents = ACTIVITY_EVENTS,
  } = args || {}
  const timerId = useRef<NodeJS.Timeout | null>(null)
  const [inactiveState, setInactiveState] = useState<boolean>(false)

  const clearTimer = useCallback(() => {
    if (timerId.current) {
      clearTimeout(timerId.current)
    }
    timerId.current = null
  }, [])

  const handleEnableInactiveState = useCallback(() => {
    clearTimer()
    setInactiveState(true)
  }, [clearTimer])

  const setTimer = useCallback(() => {
    timerId.current = setTimeout(
      handleEnableInactiveState,
      timeoutInSeconds * 1000
    )
  }, [handleEnableInactiveState, timeoutInSeconds])

  const handleUserInteraction = useCallback(() => {
    setInactiveState(false)
    clearTimer()
    setTimer()
  }, [clearTimer, setTimer])

  const reset = useCallback(() => {
    setInactiveState(false)
    clearTimer()
    setTimer()
  }, [clearTimer, setTimer])

  useEffect(() => {
    if (disable) {
      return undefined
    }
    setTimer()

    activityEvents.forEach((event: string) =>
      window.addEventListener(event, handleUserInteraction, true)
    )

    return () => {
      clearTimer()
      activityEvents.forEach((event: string) =>
        window.removeEventListener(event, handleUserInteraction, true)
      )
    }
  }, [disable, activityEvents])

  const isInactive = disable ? false : inactiveState

  return useMemo(
    () => ({
      isInactive,
      reset,
    }),
    [isInactive, reset]
  )
}

export default useDetectInactivity
